import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
export type Language = 'en' | 'vi';
export const DICTIONARY = {
  en: {
    login_title: "Welcome back",
    login_subtitle: "Enter your credentials to access Cloudface Admin.",
    auth_login_title: "Admin Portal",
    auth_login_desc: "Please enter your credentials to continue.",
    otp_helper: "Leave blank if not enabled",
    email: "Email address",
    password: "Password",
    remember_me: "Remember me",
    forgot_password: "Forgot password?",
    sign_in: "Sign In",
    signing_in: "Signing in...",
    no_account: "Don't have an account?",
    create_account: "Create an account",
    otp_title: "Two-Factor Authentication",
    otp_subtitle: "Please enter the 6-digit code sent to your device.",
    verify: "Verify",
    verifying: "Verifying...",
    resend_code: "Resend Code",
    resend_in: "Resend in {{s}}s",
    register_title: "Create an account",
    register_subtitle: "Join Cloudface and start managing your enterprise.",
    full_name: "Full Name",
    confirm_password: "Confirm Password",
    agree_terms: "I agree to the Terms of Service and Privacy Policy",
    already_have_account: "Already have an account?",
    register_button: "Register Now",
    registering: "Registering...",
    home_hero_title: "The Enterprise Intelligence Hub",
    home_hero_subtitle: "Scale your business with Cloudface's next-generation admin portal. Built for speed, security, and global teams.",
    access_portal: "Access Admin Portal",
    invalid_email: "Please enter a valid email address",
    password_min: "Password must be at least 8 characters",
    password_mismatch: "Passwords do not match",
    terms_required: "You must agree to the terms",
    otp_required: "Please enter the full 6-digit code",
    logout: "Logout",
    welcome_dashboard: "Welcome back",
    active_sessions: "Active Sessions",
    system_status: "System Status",
    user_overview: "User Overview",
    online: "Online",
    system_performance: "System Performance",
    view_all: "View All",
    connection_test: "Run Connection Test",
    test_succeeded: "Connection test succeeded",
    test_failed: "Connection test failed",
    api_key_rotated: "API Key Rotated",
    // Enterprise RBAC & Governance
    role_admin: "Administrator",
    role_manager: "Manager",
    role_viewer: "Viewer",
    role_user: "Standard User",
    workspace_switcher: "Switch Workspace",
    compliance_status: "Compliance Status",
    risk_score: "AI Risk Score",
    eu_ai_act_check: "EU AI Act Compliance",
    pii_masking_active: "PII Masking Active",
    data_residency_flag: "Data Residency",
    residency_eu: "European Union",
    residency_us: "United States",
    residency_apac: "Asia Pacific",
    roadmap_title: "Enterprise Roadmap",
    phase_security: "Security & Vault",
    phase_scalability: "Multi-tenant Clusters",
    phase_governance: "D1 Governance Hub",
    // Agents Module
    agents_title: "Manage Agents",
    create_agent: "Create New Agent",
    view_grid: "Grid View",
    view_table: "Table View",
    search_agents: "Search agents...",
    filter_all: "All",
    filter_active: "Active",
    filter_draft: "Draft",
    filter_marketplace: "Marketplace",
    tab_basic: "Basic Info",
    tab_ai: "AI Config",
    tab_tools: "Tools & Skills",
    tab_deploy: "Deploy & Sell",
    agent_name: "Agent Name",
    display_name: "Display Name",
    agent_id: "Agent ID",
    role: "Role",
    model: "LLM Model",
    temperature: "Temperature",
    system_prompt: "System Prompt",
    test_agent: "Test Agent",
    deploy_agent: "Deploy",
    monetize: "Monetize",
    price: "Monthly Price",
    success_rate: "Success Rate",
    avg_cost: "Avg. Cost",
    save_agent: "Save Agent",
    cancel: "Cancel",
    cloning: "Cloning...",
    deleting: "Deleting...",
    runs: "Total Runs",
    // Audit Logs Module
    audit_logs: "Audit Logs",
    timestamp: "Timestamp",
    event_type: "Event Type",
    user_id: "User",
    severity: "Severity",
    security_event: "Security",
    system_event: "System",
    agent_event: "Agent",
    info: "Info",
    warning: "Warning",
    critical: "Critical",
    search_logs: "Search audit trails...",
    view_all_logs: "View All Logs",
    // Prompts Module
    prompts_title: "Prompt Studio",
    create_prompt: "Create New Prompt",
    search_prompts: "Search prompts...",
    prompt_editor: "Prompt Editor",
    playground: "Playground",
    variables: "Variables",
    version_history: "Version History",
    tokens: "Tokens",
    latency: "Latency",
    visibility: "Visibility",
    private: "Private",
    team: "Team",
    public: "Public",
    optimize: "Optimize",
    fork: "Fork",
    archive: "Archive",
    chains: "Chains",
    templates: "Templates",
    // Tools Module
    tools_title: "Enterprise Tools",
    create_tool: "Create New Tool",
    import_openapi: "Import OpenAPI",
    search_tools: "Search tools or APIs...",
    tool_editor: "Tool Studio",
    tab_tech: "Technical",
    tab_permissions: "Permissions",
    tab_analytics: "Analytics",
    endpoint: "Endpoint",
    method: "Method",
    auth_type: "Auth Type",
    json_schema: "JSON Schema",
    calls: "Total Calls",
    avg_cost_per_call: "Avg. Cost/Call",
    pii_masking: "PII Masking",
    ip_whitelist: "IP Whitelist",
    built_in: "Built-in",
    custom_tool: "Custom Tool",
    connected: "Connected",
    disconnected: "Disconnected",
    test_run: "Test Run",
    save_tool: "Save Tool",
    // Integrations Module
    integrations_title: "Manage Connections",
    create_integration: "Add New Connection",
    import_file: "Import File",
    total_keys: "Total Keys",
    active_connections: "Active",
    expiring_soon: "Expiring Soon",
    category_llm: "LLM",
    category_crm: "CRM",
    category_productivity: "Productivity",
    category_finance: "Finance",
    category_custom: "Custom",
    status_connected: "Connected",
    status_expired: "Expired",
    status_rate_limited: "Rate-limited",
    settings_tab_workspaces: "Workspaces",
    settings_tab_team: "Team Management",
    live_events: "Live System Events",
    event_source: "Source",
    event_description: "Description",
    // Phase 15 Additions
    privacy_title: "Privacy Policy",
    terms_title: "Terms of Service",
    help_title: "Help Center",
    docs_title: "Documentation",
    contact_support: "Contact Support",
    frequently_asked_questions: "Frequently Asked Questions",
    last_updated: "Last Updated",
    handover_title: "Project Handover",
    handover_desc: "Production handover resources and system documentation.",
  },
  vi: {
    login_title: "Chào mừng trở lại",
    login_subtitle: "Nhập thông tin của bạn để truy cập Cloudface Admin.",
    auth_login_title: "Đăng nhập quản trị",
    auth_login_desc: "Vui lòng nhập thông tin để tiếp tục.",
    otp_helper: "Để trống nếu chưa bật",
    email: "Địa chỉ Email",
    password: "Mật khẩu",
    remember_me: "Ghi nhớ đăng nhập",
    forgot_password: "Quên mật khẩu?",
    sign_in: "Vào hệ thống",
    signing_in: "Đang đăng nhập...",
    no_account: "Chưa có tài khoản?",
    create_account: "Tạo tài khoản mới",
    otp_title: "Xác thực 2 lớp",
    otp_subtitle: "Vui lòng nhập mã 6 chữ số đã được gửi tới thiết bị của bạn.",
    verify: "Xác nhận",
    verifying: "Đang xác nhận...",
    resend_code: "Gửi lại mã",
    resend_in: "Gửi lại sau {{s}} giây",
    register_title: "Tạo tài khoản",
    register_subtitle: "Gia nhập Cloudface và bắt đầu quản lý doanh nghiệp của bạn.",
    full_name: "Họ và tên",
    confirm_password: "Xác nhận mật khẩu",
    agree_terms: "Tôi đồng ý với Điều khoản Dịch vụ và Chính sách Bảo mật",
    already_have_account: "Đã có tài khoản?",
    register_button: "Đăng ký ngay",
    registering: "Đang đăng ký...",
    home_hero_title: "Trung tâm Trí tuệ Doanh nghiệp",
    home_hero_subtitle: "Phát triển doanh nghiệp với cổng quản trị thế hệ mới của Cloudface. Tốc độ, bảo mật và dành cho đội ngũ toàn cầu.",
    access_portal: "Truy cập Hệ thống",
    invalid_email: "Vui lòng nhập email hợp lệ",
    password_min: "Mật khẩu phải có ít nhất 8 ký tự",
    password_mismatch: "Mật khẩu xác nhận không khớp",
    terms_required: "Bạn phải đồng ý với điều khoản",
    otp_required: "Vui lòng nhập đủ 6 chữ số",
    logout: "Đăng xuất",
    welcome_dashboard: "Chào mừng trở lại",
    active_sessions: "Phiên hoạt động",
    system_status: "Trạng thái hệ thống",
    user_overview: "Tổng quan người dùng",
    online: "Trực tuyến",
    system_performance: "Hiệu năng Hệ thống",
    view_all: "Xem Tất cả",
    connection_test: "Kiểm tra Kết nối",
    test_succeeded: "Kiểm tra kết nối thành công",
    test_failed: "Kiểm tra kết nối thất bại",
    api_key_rotated: "Khóa API đã được Làm mới",
    role_admin: "Quản trị viên",
    role_manager: "Quản lý",
    role_viewer: "Người xem",
    role_user: "Người dùng chuẩn",
    workspace_switcher: "Đổi không gian làm việc",
    compliance_status: "Trạng thái Tuân thủ",
    risk_score: "Điểm rủi ro AI",
    eu_ai_act_check: "Tuân thủ EU AI Act",
    pii_masking_active: "Đang ẩn dữ liệu nhạy cảm (PII)",
    data_residency_flag: "Vùng lưu trữ dữ liệu",
    residency_eu: "Liên minh Châu Âu",
    residency_us: "Hoa Kỳ",
    residency_apac: "Châu Á Thái Bình Dương",
    roadmap_title: "Lộ trình Phát triển",
    phase_security: "Bảo mật & Kho khóa",
    phase_scalability: "Cụm máy chủ Đa nền tảng",
    phase_governance: "Trung tâm Quản trị D1",
    agents_title: "Quản lý Agents",
    create_agent: "Tạo Agent Mới",
    view_grid: "Dạng Lưới",
    view_table: "Dạng Bảng",
    search_agents: "Tìm kiếm agent...",
    filter_all: "Tất cả",
    filter_active: "Hoạt động",
    filter_draft: "Bản nháp",
    filter_marketplace: "Chợ ứng dụng",
    tab_basic: "Thông tin cơ bản",
    tab_ai: "Cấu hình AI",
    tab_tools: "Công cụ & Kỹ năng",
    tab_deploy: "Triển khai & Bán",
    agent_name: "Tên Agent",
    display_name: "Tên hiển thị",
    agent_id: "Agent ID",
    role: "Vai trò",
    model: "Mô hình LLM",
    temperature: "Độ sáng tạo",
    system_prompt: "Lời nhắc hệ thống",
    test_agent: "Thử nghiệm",
    deploy_agent: "Triển khai",
    monetize: "Kiếm tiền",
    price: "Giá hàng tháng",
    success_rate: "Tỉ lệ thành công",
    avg_cost: "Chi phí TB",
    save_agent: "Lưu Agent",
    cancel: "Hủy bỏ",
    cloning: "Đang sao chép...",
    deleting: "Đang xóa...",
    runs: "Tổng lượt chạy",
    audit_logs: "Nhật ký hệ thống",
    timestamp: "Thời gian",
    event_type: "Loại sự kiện",
    user_id: "Người dùng",
    severity: "Mức độ",
    security_event: "Bảo mật",
    system_event: "Hệ thống",
    agent_event: "Agent",
    info: "Thông tin",
    warning: "Cảnh báo",
    critical: "Nghiêm trọng",
    search_logs: "Tìm kiếm nhật ký...",
    view_all_logs: "Xem tất cả nhật ký",
    prompts_title: "Studio Lời nhắc",
    create_prompt: "Tạo Lời nhắc Mới",
    search_prompts: "Tìm kiếm lời nhắc...",
    prompt_editor: "Trình chỉnh sửa",
    playground: "Sân chơi Thử nghiệm",
    variables: "Biến số",
    version_history: "Lịch sử phiên bản",
    tokens: "Tokens",
    latency: "Độ trễ",
    visibility: "Chế độ hiển thị",
    private: "Riêng tư",
    team: "Nội bộ",
    public: "Công khai",
    optimize: "Tối ưu hóa",
    fork: "Sao chép",
    archive: "Lưu trữ",
    chains: "Chuỗi",
    templates: "Mẫu",
    tools_title: "Công cụ Hệ thống",
    create_tool: "Tạo Tool Mới",
    import_openapi: "Nhập OpenAPI",
    search_tools: "Tìm kiếm tool hoặc API...",
    tool_editor: "Studio Công cụ",
    tab_tech: "Kỹ thuật",
    tab_permissions: "Quyền truy cập",
    tab_analytics: "Phân tích",
    endpoint: "Điểm cuối (Endpoint)",
    method: "Phương thức",
    auth_type: "Loại xác thực",
    json_schema: "Cấu trúc JSON",
    calls: "Tổng lượt gọi",
    avg_cost_per_call: "Chi phí TB/Lượt",
    pii_masking: "Ẩn dữ liệu nhạy cảm (PII)",
    ip_whitelist: "Danh sách IP trắng",
    built_in: "Tích hợp sẵn",
    custom_tool: "Tùy chỉnh",
    connected: "Đã kết nối",
    disconnected: "Mất kết nối",
    test_run: "Chạy thử",
    save_tool: "Lưu Công cụ",
    integrations_title: "Quản lý Kết nối",
    create_integration: "Thêm Kết nối Mới",
    import_file: "Nhập File",
    total_keys: "Tổng số Khóa",
    active_connections: "Đang hoạt động",
    expiring_soon: "Sắp hết hạn",
    category_llm: "LLM",
    category_crm: "CRM",
    category_productivity: "Năng suất",
    category_finance: "Tài chính",
    category_custom: "Tùy chỉnh",
    status_connected: "Đã kết nối",
    status_expired: "Đã hết hạn",
    status_rate_limited: "Vượt giới hạn",
    settings_tab_workspaces: "Không gian làm việc",
    settings_tab_team: "Quản lý đội ngũ",
    live_events: "Sự kiện hệ thống trực tiếp",
    event_source: "Nguồn",
    event_description: "Mô tả",
    // Phase 15 Additions
    privacy_title: "Chính sách Bảo mật",
    terms_title: "Điều khoản Dịch vụ",
    help_title: "Trung tâm Trợ giúp",
    docs_title: "Tài liệu Kỹ thuật",
    contact_support: "Liên hệ Hỗ trợ",
    frequently_asked_questions: "Câu hỏi thường gặp",
    last_updated: "Cập nhật mới nhất",
    handover_title: "Bàn giao Dự án",
    handover_desc: "Tài liệu bàn giao và hướng dẫn vận hành hệ thống.",
  }
};
interface I18nState {
  lang: Language;
  setLang: (lang: Language) => void;
}
export const useI18nStore = create<I18nState>()(
  persist(
    (set) => ({
      lang: 'vi', // Vietnamese as professional default
      setLang: (lang) => set({ lang }),
    }),
    {
      name: 'cloudface-language-pref',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
export function useTranslation() {
  const lang = useI18nStore((s) => s.lang);
  const t = (key: keyof typeof DICTIONARY['en']) => {
    return DICTIONARY[lang][key] || DICTIONARY['en'][key] || key;
  };
  return { t, lang };
}